import type { SlideProps } from "@/engine/app/offsite-config";

/**
 * Zeitplan beider Tage nebeneinander — steht zu Beginn des KI-Impulses.
 *
 * Zweck ist die Orientierung über den Tagescut hinweg: Tag 1 bringt nur den
 * KI-Impuls, die Teamarbeit (Einteilung, Themenfindung, Prompten) beginnt
 * am Morgen von Tag 2 (Umplanung 24.09. mittags). Wer den ganzen Bogen
 * sieht, geht mit einer Erwartung ins Team-Event.
 *
 * `uns` markiert die Blöcke, die zu diesem Deck gehören — der Strategie-Teil
 * am Vormittag läuft ohne diese App.
 */

interface Block {
  zeit: string;
  titel: string;
  dauer?: string;
  uns?: boolean;
}

const TAG1: Block[] = [
  { zeit: "9:00", titel: "Begrüßung und Start" },
  { zeit: "9:15", titel: "Strategie SVO 2030 — Bearbeitung" },
  { zeit: "12:00", titel: "Mittag" },
  { zeit: "13:00", titel: "Ergebnisse Strategie" },
  { zeit: "14:00", titel: "KI-Impuls", dauer: "60 Min", uns: true },
  { zeit: "15:00", titel: "Einchecken" },
  { zeit: "15:30", titel: "Team-Event · Thansen Games" },
  { zeit: "19:00", titel: "Abendessen" },
];

const TAG2: Block[] = [
  { zeit: "9:00", titel: "Eure Themen, kurzer Einstieg", dauer: "5 Min", uns: true },
  { zeit: "9:05", titel: "Prompten — euer Steckbrief", dauer: "30 Min", uns: true },
  { zeit: "9:35", titel: "Einstieg SVO OS", dauer: "15 Min", uns: true },
  { zeit: "9:50", titel: "Teilergebnisse, KI-Bewertung", dauer: "15 Min", uns: true },
  { zeit: "10:05", titel: "Pause", dauer: "30 Min" },
  { zeit: "10:35", titel: "Ergebnisse anschauen & diskutieren", dauer: "60 Min", uns: true },
  { zeit: "11:35", titel: "Weitere Themen — offene Runde", dauer: "15 Min", uns: true },
  { zeit: "11:50", titel: "Feedback, Erkenntnisse, Ausblick" },
  { zeit: "13:00", titel: "Heimfahrt" },
];

function Spalte({ tag, datum, bloecke }: { tag: string; datum: string; bloecke: Block[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          paddingBottom: 8,
          borderBottom: "2px solid rgba(255,255,255,0.18)",
        }}
      >
        <span
          style={{
            fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
            fontWeight: 700,
            color: "var(--svo-blau)",
          }}
        >
          {tag}
        </span>
        <span style={{ fontSize: "clamp(0.8rem, 1.1vw, 1rem)", opacity: 0.6 }}>{datum}</span>
      </div>

      {bloecke.map((b) => (
        <div
          key={b.zeit + b.titel}
          style={{
            display: "grid",
            gridTemplateColumns: "72px 1fr auto",
            alignItems: "baseline",
            gap: 12,
            padding: "7px 12px",
            borderRadius: 10,
            // Unsere Blöcke stehen hervor, der Rest bleibt Kontext.
            background: b.uns ? "rgba(0,141,247,0.16)" : "transparent",
            border: b.uns
              ? "1px solid rgba(0,141,247,0.45)"
              : "1px solid transparent",
            opacity: b.uns ? 1 : 0.62,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "clamp(0.85rem, 1.15vw, 1.05rem)",
              fontVariantNumeric: "tabular-nums",
              color: b.uns ? "var(--svo-blau)" : "rgba(255,255,255,0.7)",
            }}
          >
            {b.zeit}
          </span>
          <span
            style={{
              fontSize: "clamp(0.9rem, 1.25vw, 1.15rem)",
              fontWeight: b.uns ? 600 : 400,
            }}
          >
            {b.titel}
          </span>
          {b.dauer && (
            <span
              style={{
                fontSize: "clamp(0.7rem, 0.95vw, 0.9rem)",
                opacity: 0.65,
                whiteSpace: "nowrap",
              }}
            >
              {b.dauer}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function Slide01bAgenda(_: SlideProps) {
  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1">Ablauf</div>
        <h1 className="deck-title rise rise-2" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)" }}>
          Zwei Tage — <span className="accent">ein roter Faden</span>
        </h1>
        <p className="deck-sub rise rise-3" style={{ fontSize: "clamp(0.9rem, 1.3vw, 1.2rem)" }}>
          Heute der Impuls, morgen früh geht es in die Teams: Thema finden,
          prompten, und die KI baut euren Prototyp. Blau markiert ist unser Teil.
        </p>
      </header>

      <div
        className="rise rise-4"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "clamp(24px, 4vw, 64px)",
          width: "100%",
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >
        <Spalte tag="Tag 1" datum="Strategie · KI-Impuls · Team-Event" bloecke={TAG1} />
        <Spalte tag="Tag 2" datum="Teams · Bauen · Ergebnisse" bloecke={TAG2} />
      </div>
    </div>
  );
}
